/**
 * Experiment / feature-flag keys — OptiGrowth-decider experiments and any
 * remaining PostHog flags.
 *
 * One source of truth for flag names so frontend, backend, and PostHog UI all
 * reference the same string. When adding a new experiment, document the flag
 * in `docs/experiments/<flag-key>.md`.
 */
export const EXPERIMENT_FLAGS = {
  /**
   * welcome-screen-v2: 2-arm A/B on the Screens-page welcome screen. Server-decided
   * via the OptiGrowth decider (NOT PostHog) and consumed through
   * `useExperimentVariant`, which persists the arm write-once to
   * `company.experiments['welcome-screen-v2']` — sticky per account, forever.
   *
   * Arms: `control` (the current welcome screen, Classic/V2 per
   * VITE_USE_CLASSIC_WELCOME_SCREEN) | `b` (WelcomeScreenTest — the platform-ticker
   * screen). Anything else, and not-enrolled, renders control.
   *
   * QA preview (non-prod): `?ws=b` for short, or the generic
   * `?force_variant_welcome-screen-v2=b`.
   *
   * WHY A NEW KEY (2026-09-08): the old `welcome-screen` key is spent. Its OptiGrowth
   * sandbox record was created with arms `control`/`a` instead of `control`/`b`, the
   * code only renders the ticker for `b`, and arm names FREEZE once assignments exist.
   * The platform has no delete and `status=ended` is terminal, so the runbook's rule
   * applies: "re-running means a new key." Accounts still carrying a legacy
   * `experiments['welcome-screen']` value (5,785 on `b` in prod, from the PostHog era)
   * no longer match anything and get freshly randomized under this key.
   * See `docs/experiments/welcome-screen-v2.md`.
   */
  WELCOME_SCREEN_V2: 'welcome-screen-v2',

  /**
   * add-screen-cta-v1: 2-arm (control/treatment) copy test on the WelcomeScreen
   * "Add Screen" CTA button label. Server-decided via the OptiGrowth decider
   * (NOT PostHog) and consumed through `useExperimentVariant`. Arms: `control`
   * (current "Add Screen") | `treatment` ("Add Your First Screen"). qa3g-only
   * harness test. See `docs/experiments/add-screen-cta-v1.md`.
   */
  ADD_SCREEN_CTA: 'add-screen-cta-v1',

  /**
   * sub-wizard-default-term-v1: 2-arm 50/50 test on which term the subscription wizard's
   * Configure step (step 2) opens on for accounts that have never been billed. Server-decided
   * via the OptiGrowth decider (NOT PostHog), consumed through `useExperimentVariant` in
   * `useSubscriptionWizard`. Arms: `control` (opens on Yearly, today's default) | `monthly`
   * (opens on Monthly). The plan step is identical in both arms, and a term the user picks on
   * its toggle always wins. See `docs/experiments/sub-wizard-default-term-v1.md`.
   */
  SUB_WIZARD_DEFAULT_TERM: 'sub-wizard-default-term-v1',
} as const;

export type ExperimentFlagKey = (typeof EXPERIMENT_FLAGS)[keyof typeof EXPERIMENT_FLAGS];

/**
 * Surfaces where VISITOR-unit experiments (anonymous, pre-auth — OptiGrowth
 * decider keyed on the og_vid browser id, not companyId) can run. The server
 * allowlists these: `resolveVisitorExperiments(surface, visitorId)` only
 * resolves experiments whose config names the requested surface. Extend the
 * enum when a new pre-auth surface gets experiments; each entry needs a
 * `useVisitorExperiments(surface)` call site on that page.
 * Spec: claude-dev/kate/2026-07-21-optigrowth-visitor-experiments/ (harness root).
 */
export const VISITOR_EXPERIMENT_SURFACES = {
  SIGNUP_PAGE: 'SIGNUP_PAGE',
} as const;

export type VisitorExperimentSurface =
  (typeof VISITOR_EXPERIMENT_SURFACES)[keyof typeof VISITOR_EXPERIMENT_SURFACES];

/** One resolved visitor-experiment assignment: which arm this og_vid gets for `key`. */
export interface VisitorAssignment {
  key: string;
  arm: string;
}
